/**
 * A/B Experiments Configuration and Logic
 *
 * Manages feature flags and experiments with guardrails:
 * - Sample Ratio Mismatch (SRM) checks
 * - Minimum sample/period gates
 * - Kill-switch and soft pause controls
 * - Results panel integration
 */

import { getFeatureFlag, isPostHogReady, capture } from './posthog';
import { fireEvent } from '@/lib/analytics';
import { supabase } from '@/integrations/supabase/client';

// Experiment definitions
export const EXP_BIN_CTA_V1 = 'exp_bin_cta_v1';

// Variant definitions
export const BIN_CTA_VARIANTS = {
  CONTROL_BUY_NOW: 'control',
  VARIANT_GET_IT_NOW: 'variant',
} as const;

type CtaVariant = (typeof BIN_CTA_VARIANTS)[keyof typeof BIN_CTA_VARIANTS];

interface ExperimentOverride {
  value: string;
  expires: number;
}

/**
 * Get experiment override from localStorage
 */
function getStoredOverride(expKey: string): string | null {
  try {
    const stored = localStorage.getItem(`exp:${expKey}`);
    if (!stored) return null;

    const override: ExperimentOverride = JSON.parse(stored);

    // Check if expired
    if (Date.now() > override.expires) {
      localStorage.removeItem(`exp:${expKey}`);
      return null;
    }

    return override.value;
  } catch {
    return null;
  }
}

/**
 * Store experiment override in localStorage
 */
function storeOverride(expKey: string, value: string, ttlMs: number = 24 * 60 * 60 * 1000): void {
  try {
    const override: ExperimentOverride = {
      value,
      expires: Date.now() + ttlMs,
    };
    localStorage.setItem(`exp:${expKey}`, JSON.stringify(override));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Clear experiment override
 */
function clearOverride(expKey: string): void {
  try {
    localStorage.removeItem(`exp:${expKey}`);
  } catch {
    // Ignore storage errors
  }
}

/**
 * Handle URL-based experiment overrides
 */
function handleUrlOverrides(): void {
  const url = new URL(window.location.href);
  const params = url.searchParams;

  // Handle exp_bin_cta_v1 overrides
  const binCtaOverride = params.get(EXP_BIN_CTA_V1);
  if (binCtaOverride) {
    if (binCtaOverride === 'off') {
      clearOverride(EXP_BIN_CTA_V1);
    } else if (Object.values(BIN_CTA_VARIANTS).includes(binCtaOverride as CtaVariant)) {
      storeOverride(EXP_BIN_CTA_V1, binCtaOverride);
    }

    // Clean URL
    params.delete(EXP_BIN_CTA_V1);
    window.history.replaceState({}, '', url.toString());
  }
}

// Initialize URL overrides on load
if (typeof window !== 'undefined') {
  handleUrlOverrides();
}

/**
 * Get experiment variant with fallback logic
 */
export function getVariant(expKey: string, options: { fallback?: string } = {}): string {
  const { fallback = BIN_CTA_VARIANTS.CONTROL_BUY_NOW } = options;

  // Check for QA override first
  const override = getStoredOverride(expKey);
  if (override) {
    return override;
  }

  // Try PostHog feature flag
  if (isPostHogReady()) {
    const variant = getFeatureFlag(expKey, fallback);
    return variant;
  }

  // Fallback to 50/50 split based on session
  try {
    const sessionId = sessionStorage.getItem('session_id') || Math.random().toString(36);
    sessionStorage.setItem('session_id', sessionId);

    // Simple hash-based assignment
    const hash = sessionId.split('').reduce((a, b) => {
      a = (a << 5) - a + b.charCodeAt(0);
      return a & a;
    }, 0);

    return Math.abs(hash) % 2 === 0
      ? BIN_CTA_VARIANTS.CONTROL_BUY_NOW
      : BIN_CTA_VARIANTS.VARIANT_GET_IT_NOW;
  } catch {
    return fallback;
  }
}

/**
 * Get BIN CTA configuration based on experiment
 */
export function getBinCtaConfig(domain?: string): { variant: CtaVariant; label: string } {
  const variant = getVariant(EXP_BIN_CTA_V1) as CtaVariant;

  const config = {
    [BIN_CTA_VARIANTS.CONTROL_BUY_NOW]: { label: 'Buy Now' },
    [BIN_CTA_VARIANTS.VARIANT_GET_IT_NOW]: { label: 'Get It Now' },
  };

  return {
    variant,
    label: config[variant]?.label || config[BIN_CTA_VARIANTS.CONTROL_BUY_NOW].label,
  };
}

/**
 * Track experiment exposure
 */
export function trackExposure(
  expKey: string,
  variant: string,
  properties: Record<string, any> = {},
): void {
  fireEvent('ab_exposure', {
    experiment: expKey,
    variant,
    ...properties,
  });
}

/**
 * Track experiment event (conversion, click, etc.)
 */
export function trackExperimentEvent(
  expKey: string,
  variant: string,
  event: string,
  properties: Record<string, any> = {},
): void {
  fireEvent('ab_event', {
    experiment: expKey,
    variant,
    event,
    ...properties,
  });
}

/**
 * Force experiment variant (for QA/debugging)
 */
export function forceVariant(expKey: string, variant: string | null): void {
  if (variant === null) {
    clearOverride(expKey);
  } else {
    storeOverride(expKey, variant, 24 * 60 * 60 * 1000); // 24 hours
  }
}

/**
 * Get current experiment state for debugging
 */
export function getExperimentDebugInfo(expKey: string): {
  variant: string;
  source: 'override' | 'posthog' | 'fallback';
  posthogReady: boolean;
} {
  const override = getStoredOverride(expKey);
  const posthogReady = isPostHogReady();

  if (override) {
    return { variant: override, source: 'override', posthogReady };
  }

  if (posthogReady) {
    const variant = getFeatureFlag(expKey, BIN_CTA_VARIANTS.CONTROL_BUY_NOW);
    return { variant, source: 'posthog', posthogReady };
  }

  const fallbackVariant = getVariant(expKey);
  return { variant: fallbackVariant, source: 'fallback', posthogReady };
}

// ============================================================================
// EXPERIMENT REGISTRY & GUARDRAILS
// ============================================================================

export interface ExperimentConfig {
  name: string;
  flagKey: string;
  expectedSplit: number;
  startAt: string;
  status: 'running' | 'paused' | 'killed';
}

export interface ExperimentRegistry {
  [key: string]: ExperimentConfig;
}

let registryCache: { data: ExperimentRegistry | null; fetchedAt: number } = {
  data: null,
  fetchedAt: 0,
};

const REGISTRY_CACHE_TTL = 10 * 60 * 1000; // 10 minutes

/**
 * Fetch experiments registry from admin_settings
 */
export async function getRegistry(): Promise<ExperimentRegistry> {
  const now = Date.now();

  if (registryCache.data && now - registryCache.fetchedAt < REGISTRY_CACHE_TTL) {
    return registryCache.data;
  }

  try {
    const { data, error } = await supabase
      .from('admin_settings')
      .select('value')
      .eq('key', 'experiments_registry')
      .maybeSingle();

    if (error) throw error;

    const registry: ExperimentRegistry = data?.value ? JSON.parse(data.value) : {};

    registryCache = { data: registry, fetchedAt: now };
    return registry;
  } catch (error) {
    console.error('Error fetching experiments registry:', error);
    return {};
  }
}

/**
 * Update experiment status (admin only)
 */
export async function setStatus(
  id: string,
  status: 'running' | 'paused' | 'killed',
): Promise<void> {
  const registry = await getRegistry();

  if (!registry[id]) {
    throw new Error(`Experiment ${id} not found in registry`);
  }

  registry[id].status = status;

  const functionsBase =
    import.meta.env.VITE_FUNCTIONS_BASE || 'https://ifxetmmcscffwlijpdfs.supabase.co/functions/v1';

  const response = await fetch(`${functionsBase}/admin-settings-set`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'x-admin': '1',
    },
    body: JSON.stringify({
      key: 'experiments_registry',
      value: JSON.stringify(registry),
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to update experiment status');
  }

  // Clear cache
  registryCache = { data: null, fetchedAt: 0 };

  // Track status change
  fireEvent('exp_status_change', { id, to: status });
}

/**
 * Get guardrail thresholds (client-side defaults)
 */
export function getGuardrails(id: string, config?: ExperimentConfig) {
  return {
    expectedSplit: config?.expectedSplit ?? 0.5,
    minSample: 400,
    minDays: 3,
    srmPValue: 0.001,
  };
}

/**
 * Check if experiment should expose users to variants
 */
export async function shouldExpose(flagKey: string): Promise<boolean> {
  const registry = await getRegistry();

  // Find experiment by flagKey
  const experiment = Object.values(registry).find((exp) => exp.flagKey === flagKey);

  if (!experiment) {
    return true; // No config = no restrictions
  }

  // Killed = force CONTROL for everyone
  if (experiment.status === 'killed') {
    return false;
  }

  // Paused = no new exposures (existing sessions keep variant via localStorage)
  if (experiment.status === 'paused') {
    // Check if user already has a variant stored
    const storedOverride = getStoredOverride(flagKey);
    return !!storedOverride;
  }

  return true;
}

/**
 * Clear registry cache (for admin updates)
 */
export function clearRegistryCache(): void {
  registryCache = { data: null, fetchedAt: 0 };
}
